/**
 * SellerRegistrationFlow — top-level orchestrator for the seller
 * self-registration wizard.
 *
 * Entry point: `/sellers/register` (Start step — not inside the wizard shell)
 * Wizard steps: Profile → Email-OTP → Phone-OTP → Review → Submitted
 *
 * Two routing modes:
 *   1. `/sellers/register` → shows StartStep (creates a new application).
 *   2. `/sellers/register/:id` → resumes an existing application
 *      (GET /sellers/register/:id, then jumps to the server's `current_step`).
 *
 * The WizardShell (StepDots, back button) wraps the 4 inner wizard steps.
 * The Start screen and the Submitted screen are outside the shell (no dots).
 *
 * localStorage holds only the UUID — never PII (per spec).
 */
import { useState, useEffect, type ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { registerSellerRegI18n } from "@/features/seller-registration/api";
import {
  apiRegisterStart,
  apiRegisterProfile,
  apiRegisterEmailSend,
  apiRegisterEmailVerify,
  apiRegisterPhoneSend,
  apiRegisterPhoneVerify,
  apiRegisterSubmit,
  apiRegisterResume,
  apiFetchMarkets,
} from "@/features/seller-registration/api";
import {
  clearApplicationId,
  loadSavedApplicationId,
  saveApplicationId,
  type ProfileFormValues,
  type WizardStep,
} from "@/features/seller-registration/schemas";
import { WizardShell } from "@/features/seller-registration/WizardShell";
import { StartStep } from "@/features/seller-registration/steps/StartStep";
import { ProfileStep } from "@/features/seller-registration/steps/ProfileStep";
import { OtpStep } from "@/features/seller-registration/steps/OtpStep";
import { ReviewStep } from "@/features/seller-registration/steps/ReviewStep";
import { SubmittedStep } from "@/features/seller-registration/steps/SubmittedStep";

type FlowState =
  | { kind: "start" }
  | { kind: "wizard"; step: WizardStep; applicationId: string }
  | { kind: "submitted"; estimatedHours: number };

export function SellerRegistrationFlow(): ReactElement {
  registerSellerRegI18n();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();

  const [flowState, setFlowState] = useState<FlowState>({ kind: "start" });
  const [email, setEmail] = useState("");
  const [profileData, setProfileData] = useState<ProfileFormValues | null>(null);
  const [marketSlugByMarketId, setMarketSlugByMarketId] = useState<
    Record<string, string>
  >({});
  /** Slug carried from a resumed application — used to restore the market dropdown. */
  const [resumedMarketSlug, setResumedMarketSlug] = useState<string | undefined>(undefined);

  // Resume path: if URL contains /:id, fetch the application and jump to
  // the server's current_step.
  useEffect(() => {
    const urlId = params.id;
    if (!urlId) return;

    void (async () => {
      try {
        const resume = await apiRegisterResume(urlId);
        saveApplicationId(urlId);

        // Map server step string → our WizardStep enum.
        // The backend `current_step` after phone verification is
        // `ready_to_submit` (ADR-042 Decision 2 descoped the `photo_proof`
        // step, but the literal may still appear on older rows). Both, plus
        // `review`/`submitted`, land the resumer on the Review step.
        const serverStep = resume.current_step;
        let step: WizardStep = "profile";
        if (serverStep === "email_otp" || serverStep === "email-otp") {
          step = "email-otp";
        } else if (serverStep === "phone_otp" || serverStep === "phone-otp") {
          step = "phone-otp";
        } else if (
          serverStep === "photo_proof" ||
          serverStep === "ready_to_submit" ||
          serverStep === "review" ||
          serverStep === "submitted"
        ) {
          step = "review";
        }

        // Hydrate profile data from the resume response
        if (resume.profile) {
          const p = resume.profile;
          setProfileData({
            business_name: p.business_name ?? "",
            market_id: "",
            pavilion_number: p.pavilion_number ?? "",
            entrance_hint: p.entrance_hint ?? "",
            phone_e164: p.phone_e164 ?? "",
            whatsapp_phone_e164: p.whatsapp_phone_e164 ?? "",
            telegram_handle: p.telegram_handle ?? "",
          });
          // Carry the market slug so ProfileStep can restore the dropdown
          // once the markets list has loaded (market_id stays "" until then).
          if (p.market_slug) {
            setResumedMarketSlug(p.market_slug);
          }
        }

        setFlowState({ kind: "wizard", step, applicationId: urlId });
      } catch {
        // Application not found or expired — start fresh
        navigate("/sellers/register", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // ── Transitions ──────────────────────────────────────────────────────────

  function handleStarted(applicationId: string, userEmail: string): void {
    saveApplicationId(applicationId);
    setEmail(userEmail);
    setResumedMarketSlug(undefined); // fresh application — no slug to restore
    setFlowState({ kind: "wizard", step: "profile", applicationId });
    navigate(`/sellers/register/${applicationId}/profile`, { replace: true });
  }

  function handleProfileSaved(data: ProfileFormValues, marketSlug: string): void {
    setProfileData(data);
    setMarketSlugByMarketId((prev) => ({
      ...prev,
      [data.market_id]: marketSlug,
    }));
    if (flowState.kind !== "wizard") return;
    setFlowState({ ...flowState, step: "email-otp" });
  }

  function handleEmailVerified(): void {
    if (flowState.kind !== "wizard") return;
    setFlowState({ ...flowState, step: "phone-otp" });
  }

  function handlePhoneVerified(): void {
    if (flowState.kind !== "wizard") return;
    setFlowState({ ...flowState, step: "review" });
  }

  function handleSubmitted(hours: number): void {
    clearApplicationId();
    setFlowState({ kind: "submitted", estimatedHours: hours });
  }

  function handleBack(): void {
    if (flowState.kind !== "wizard") {
      navigate("/");
      return;
    }
    switch (flowState.step) {
      case "profile":
        setFlowState({ kind: "start" });
        navigate("/sellers/register", { replace: true });
        break;
      case "email-otp":
        setFlowState({ ...flowState, step: "profile" });
        break;
      case "phone-otp":
        setFlowState({ ...flowState, step: "email-otp" });
        break;
      case "review":
        setFlowState({ ...flowState, step: "phone-otp" });
        break;
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (flowState.kind === "start") {
    const savedId = loadSavedApplicationId();
    return (
      <div className="flex min-h-dvh flex-col bg-[var(--g-ground)]">
        <main className="mx-auto flex w-full max-w-[480px] flex-1 flex-col px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-16">
          <StartStep
            apiRegisterStart={apiRegisterStart}
            onStarted={handleStarted}
            savedApplicationId={savedId}
            onResume={
              savedId
                ? () => {
                    navigate(`/sellers/register/${savedId}/profile`);
                  }
                : undefined
            }
          />
        </main>
      </div>
    );
  }

  if (flowState.kind === "submitted") {
    return (
      <div className="flex min-h-dvh flex-col bg-[var(--g-ground)]">
        <main className="mx-auto flex w-full max-w-[480px] flex-1 flex-col px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-16">
          <SubmittedStep estimatedReviewHours={flowState.estimatedHours} />
        </main>
      </div>
    );
  }

  // Wizard steps
  const { step, applicationId } = flowState;

  const marketDisplayName =
    profileData?.market_id
      ? (marketSlugByMarketId[profileData.market_id] ?? profileData.market_id)
      : "";

  return (
    <WizardShell step={step} onBack={handleBack} hideBack={false}>
      {step === "profile" ? (
        <ProfileStep
          applicationId={applicationId}
          initialValues={profileData ?? undefined}
          resumedMarketSlug={resumedMarketSlug}
          apiFetchMarkets={apiFetchMarkets}
          apiRegisterProfile={apiRegisterProfile}
          onProfileSaved={handleProfileSaved}
          onBack={handleBack}
        />
      ) : step === "email-otp" ? (
        <OtpStep
          channel="email"
          sentTo={email}
          applicationId={applicationId}
          apiSend={apiRegisterEmailSend}
          apiVerify={apiRegisterEmailVerify}
          onVerified={handleEmailVerified}
        />
      ) : step === "phone-otp" ? (
        <OtpStep
          channel="phone"
          sentTo={profileData?.phone_e164 ?? ""}
          applicationId={applicationId}
          apiSend={apiRegisterPhoneSend}
          apiVerify={apiRegisterPhoneVerify}
          onVerified={handlePhoneVerified}
        />
      ) : (
        <ReviewStep
          applicationId={applicationId}
          email={email}
          profileData={profileData ?? {
            business_name: "",
            market_id: "",
            pavilion_number: "",
            entrance_hint: "",
            phone_e164: "",
            whatsapp_phone_e164: "",
            telegram_handle: "",
          }}
          marketDisplayName={marketDisplayName}
          apiRegisterSubmit={apiRegisterSubmit}
          onSubmitted={handleSubmitted}
          onEditProfile={() => {
            if (flowState.kind === "wizard") {
              setFlowState({ ...flowState, step: "profile" });
            }
          }}
        />
      )}
    </WizardShell>
  );
}

export default SellerRegistrationFlow;
